import { useQuery } from 'react-query';
import CallQueueAPI from '@frontend/api-call-queue';
import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { DropdownField, FormFieldActionTypes, SkeletonLoader, useForm } from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { DialOptionInstruction } from '../types';
import { ReadOnlyInstructionField } from './read-only-instruction';
import { InstructionFieldStyles } from './styles';

export const CallQueueInstructionField = ({
  onChange,
  instructionState,
  isReadOnly,
  disabled = false,
}: {
  onChange: (payload: DialOptionInstruction) => void;
  instructionState: DialOptionInstruction;
  isReadOnly?: boolean;
  disabled?: boolean;
}) => {
  const { t } = useTranslation('phone');
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');

  const { data: callQueues = [], isLoading } = useQuery({
    queryKey: [settingsTenantLocation?.phoneTenantId, ...queryKeys.settings.listCallQueue()],
    queryFn: () =>
      CallQueueAPI.List({ tenantId: settingsTenantLocation?.phoneTenantId ?? '' }).then((res) => res.callQueues),
    select: (data) => {
      return (data ?? [])
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((callQueue) => ({
          value: callQueue.callQueueId,
          label: callQueue.name,
        }));
    },
    staleTime: 10000,
    enabled: !!settingsTenantLocation?.phoneTenantId && !isReadOnly,
  });

  const form = useForm({
    fields: {
      callQueueId: {
        type: 'dropdown',
        value: instructionState.id,
      },
    },
    fieldStateReducer: (state, action) => {
      // When any field is updated, extract all field values and notify the parent component
      if (action.type === FormFieldActionTypes.Update) {
        const payload = {
          ...instructionState,
          id: state.callQueueId.value,
        };
        onChange(payload);
      }

      return state;
    },
  });

  if (isLoading) {
    return <SkeletonLoader height={40} width={225} />;
  }

  if (isReadOnly) {
    return (
      <ReadOnlyInstructionField
        instructionName={instructionState.name ?? t('Unknown Call Queue')}
        iconName={'call-queue'}
        instructionType={t('Call Queue')}
      />
    );
  }

  return (
    <DropdownField
      {...form.getFieldProps('callQueueId')}
      name={'callQueueId'}
      label={t('Call Queue')}
      css={InstructionFieldStyles}
      disabled={disabled}
    >
      {callQueues?.map((callQueue) => (
        <DropdownField.Option key={callQueue.value} value={callQueue.value}>
          {callQueue.label}
        </DropdownField.Option>
      ))}
    </DropdownField>
  );
};
